// Removed until we can solve importing react-plotly.js not detonating the build server

import React, { useState, useEffect } from 'react'
import PlotlyChart from './PlotlyChart'

const maps = {
  'uk-wpc': {
    id: 'uk-wpc',
    name: 'UK Constituencies',
    geoJsonUrl: '/maps/wpc2024.geojson',
    areaCodeKey: 'PCON24CD',
    areaNameKey: 'PCON24NM',
    defaultMapProps: {
      center: {lon: -4, lat: 55},
      zoom: 4.5,
    }
  },
  'world': {
    id: 'world',
    name: 'World Map',
    geoJsonUrl: '/maps/world.geojson',
    areaCodeKey: 'iso_a2_eh',
    areaNameKey: 'name',
    defaultMapProps: {
      center: {lon: 5, lat: 55},
      zoom: 2.5
    }
  }
}


function Map({ data, geoJson, mapId, mapIndex, plotRef, handleRelayout }) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
  const containerRef = useRef(null)

  // Define your desired aspect ratio (height/width)
  const aspectRatio = 1.5 // e.g., 2:1 means height is 2x the width
  const maxHeight = 550

  const areaCodeKey = maps[mapId].areaCodeKey
  const areaNameKey = maps[mapId].areaNameKey
  const defaultMapProps = maps[mapId].defaultMapProps

  useEffect(() => {
    if (!containerRef.current) return

    const updateDimensions = () => {
      if (containerRef.current) {
        const maxWidth = containerRef.current.offsetWidth
        const height = Math.min(maxWidth * aspectRatio, maxHeight)
        const width = maxWidth
        setDimensions({ width, height })
      }
    }

    // Initial measurement
    updateDimensions()

    // Use ResizeObserver for container size changes
    const resizeObserver = new ResizeObserver(updateDimensions)
    resizeObserver.observe(containerRef.current)

    // Also listen to window resize as backup
    window.addEventListener('resize', updateDimensions)

    return () => {
      resizeObserver.disconnect()
      window.removeEventListener('resize', updateDimensions)
    }
  }, [aspectRatio])

  // const mapData = data.x.map((code, i) => ({
  //   constituency_code: code,
  //   count: data.y[i],
  //   name: (geoJson.features.find(f => f.properties[areaCodeKey] === code)?.properties || {})[areaNameKey]
  // }))

  const mapData = geoJson.features.map(feature => {
    const code = feature.properties[areaCodeKey]
    const count = data.y[data.x.indexOf(code)] || 0
    return {
      constituency_code: code,
      count,
      name: feature.properties[areaNameKey]
    }
  }).filter(d => d.count > 0)

  console.log({ data })

  const zmax = (() => {
    if (mapData.length === 0) return 0
    const max = Math.max(...mapData.map(d => d.count))
    return max * 0.95
  })()

  const plotData = [{
    type: "choroplethmap",
    name: "UK Constituencies",
    geojson: geoJson,
    locations: mapData.map(d => d.constituency_code),
    text: mapData.map(d => d.name),
    z: mapData.map(d => d.count),
    zmin: 0,
    zmax: zmax,
    featureidkey: `properties.${areaCodeKey}`,
    hovertemplate: '<b>%{text}</b><br>' +
                   'Count: %{z}<br>' +
                   '<extra></extra>',
    colorscale: 'Turbo',
    showscale: false
  }]

  // basic, carto-darkmatter, carto-darkmatter-nolabels, carto-positron, carto-positron-nolabels, carto-voyager, carto-voyager-nolabels, dark, light, open-street-map, outdoors, satellite, satellite-streets, streets, white-bg

  const layout = {
    autosize: false, // Turn off autosize since we're managing dimensions
    width: dimensions.width,
    height: dimensions.height,
    map: {
      ...defaultMapProps,
      projection: 'orthographic', // Globe view
      style: {
        version: 8,
        sources: {
          countries: {
            type: 'vector',
            url: 'https://demotiles.maplibre.org/tiles/tiles.json'
          }
        },
        layers: [
          {
            id: 'countries',
            type: 'line',
            source: 'countries',
            'source-layer': 'countries',
            paint: {
              'line-color': '#cccccc',
              'line-width': 1
            }
          }
        ],
        projection: {
          type: 'globe'
        }
      }
    },
    margin: { t: 0, b: 0, l: 0, r: 0 },
    paper_bgcolor: 'rgba(0,0,0,0)',
    plot_bgcolor: 'rgba(0,0,0,0)',
  }

  // Don't render until we have dimensions
  if (dimensions.width === 0) {
    return <div ref={containerRef} className="w-full" />
  }

  return (
    <div ref={containerRef} className="w-full">
      <div ref={plotRef} style={{ mixBlendMode: 'multiply' }}>
        <PlotlyChart
          data={plotData}
          layout={layout}
          config={{
            responsive: false, // Turn off responsive since we're handling it
            displayModeBar: false,
            displaylogo: false
          }}
          onUpdate={(figure) => {
            console.log({ figure })
            if (!figure) return
            setTimeout(() => handleRelayout(mapIndex, figure), 10)
          }}
        />
      </div>
    </div>
  )
}

export default function MapChart({ data, mapId = 'uk-wpc' }) {
  const [geoJson, setGeoJson] = useState(null)
  const allPlotRefs = useRef([])
  const isUnderRelayout = useRef(false)

  useEffect(() => {
    const geoJsonUrl = maps[mapId].geoJsonUrl
    fetch(geoJsonUrl)
      .then(res => res.json())
      .then(setGeoJson)
  }, [])

  if (!geoJson) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-lg">Loading map data...</div>
      </div>
    )
  }

  // Determine grid columns based on number of maps
  const numMaps = data.length
  const gridColumns = numMaps === 1 ? 1 : numMaps === 2 ? 2 : 3
  const columnClass = gridColumns === 1 ? 'column remaining' : gridColumns === 2 ? 'column half' : 'column third'

  // Chunk data into rows
  const rows = []
  for (let i = 0; i < numMaps; i += gridColumns) {
    rows.push(data.slice(i, i + gridColumns))
  }

  function handleRelayout(mapIndex, figure) {
    if (isUnderRelayout.current) return; // Prevent nested relayouts
    isUnderRelayout.current = true;

    allPlotRefs.current.forEach((ref, idx) => {
      if (idx !== mapIndex && ref && ref.querySelector('.js-plotly-plot')) {
        // get current relayout data
        const currentMap = ref.querySelector('.js-plotly-plot').layout.map
        const newMap = figure.layout.map

        if (newMap.center.lon === currentMap.center.lon && newMap.center.lat === currentMap.center.lat && newMap.zoom === currentMap.zoom) return;

        Plotly.relayout(ref.querySelector('.js-plotly-plot'), {
          'map.center': newMap.center,
          'map.zoom': newMap.zoom
        })
      }
    })

    isUnderRelayout.current = false;
  }

  return (
    <div>
      {rows.map((rowData, rowIndex) => (
        <div key={rowIndex} className='row'>
          {rowData.map((series, colIndex) => {
            const mapIndex = rowIndex * gridColumns + colIndex

            return <div key={series.name} className={columnClass}>
              { numMaps > 1 && <div>{series.name}</div> }
              <Map data={series} geoJson={geoJson} mapId={mapId} mapIndex={mapIndex} plotRef={el => allPlotRefs.current[mapIndex] = el} handleRelayout={handleRelayout} />
            </div>
        })}
        </div>
      ))}
    </div>
  )
}
